import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Scales, Spinner } from '@phosphor-icons/react';

import { API } from '@/lib/api';
import { SharedPasswordGate } from './shared/SharedPasswordGate';
import { SharedStatCards, SharedOverviewTab, SharedTimelineTab, SharedContradictionsTab, SharedDocumentsTab } from './shared/SharedTabs';

export default function SharedCaseView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const loadShared = useCallback(async (pw) => {
    setLoading(true);
    setError('');
    try {
      const url = pw ? `${API}/shared/${token}?password=${encodeURIComponent(pw)}` : `${API}/shared/${token}`;
      const { data: resp } = await axios.get(url);
      setData(resp);
      setNeedsPassword(false);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 401 && detail.includes('Password')) {
        setNeedsPassword(true);
      } else {
        setError(detail || 'Failed to load shared case');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadShared(); }, [loadShared]);

  const handlePassword = (e) => {
    e.preventDefault();
    loadShared(password);
  };

  if (loading) return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><Spinner size={32} className="animate-spin text-[#002FA7]" /></div>;

  if (needsPassword) return <SharedPasswordGate password={password} setPassword={setPassword} onSubmit={handlePassword} error={error} />;

  if (error) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="text-center"><p className="text-[#6B7280] font-ibm text-lg">{error}</p></div>
    </div>
  );

  if (!data) return null;

  const { case: caseData, timeline, contradictions, documents } = data;
  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: `Timeline (${timeline.length})` },
    { id: 'contradictions', label: `Contradictions (${contradictions.length})` },
    { id: 'documents', label: `Documents (${documents.length})` },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="shared-case-view">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scales weight="duotone" className="w-6 h-6 text-[#002FA7]" />
            <span className="font-outfit text-lg font-bold text-[#09090B]">ClarityCase AI</span>
            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-ibm">Shared Report</span>
            {data.redacted && <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-ibm" data-testid="shared-redacted-badge">Names redacted for privacy</span>}
          </div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto p-6 sm:p-8">
        <h1 className="font-outfit text-3xl font-bold text-[#09090B] mb-1" data-testid="shared-case-title">{caseData.title}</h1>
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-ibm font-semibold px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">{caseData.status?.toUpperCase()}</span>
          <span className="text-sm text-[#6B7280] font-ibm">{caseData.case_type}</span>
        </div>

        <SharedStatCards documents={documents} timeline={timeline} contradictions={contradictions} />

        <div className="flex border-b border-[#E5E7EB] mb-6">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-sm font-ibm border-b-2 transition-all -mb-px ${activeTab === t.id ? 'border-[#002FA7] text-[#002FA7] font-semibold' : 'border-transparent text-[#6B7280]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <SharedOverviewTab caseData={caseData} />}
        {activeTab === 'timeline' && <SharedTimelineTab timeline={timeline} />}
        {activeTab === 'contradictions' && <SharedContradictionsTab contradictions={contradictions} />}
        {activeTab === 'documents' && <SharedDocumentsTab documents={documents} />}

        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-700 font-ibm">
          DISCLAIMER: This report was generated by ClarityCase AI for informational purposes only. It does not constitute legal advice.
        </div>
      </div>
    </div>
  );
}
